import { forwardRef, useMemo, useEffect, Ref, HTMLAttributes } from "react";
import { List, ListImperativeAPI } from "react-window";
import { VirtualAutocompleteItem, VirtualAutocompleteOptionParams } from "./types";
import { AutocompleteRow } from "./AutocompleteRow";
import { LISTBOX_PADDING } from "./constants";

const OPTION_HEIGHT = 42;
const GROUP_HEADER_HEIGHT = 48;
const OVERSCAN_COUNT = 5;

type Props = HTMLAttributes<HTMLElement> & {
  internalListRef: Ref<ListImperativeAPI>;
  onItemsBuilt: (optionIndexMap: Map<string, number>) => void;
};

// This component handles the virtualized rendering of the listbox for the
// VirtualizedAutocomplete component. It flattens the groups and options into
// a single list of items, calculates heights, and renders rows as needed.
// It then uses List from react-window to efficiently render only the visible items.
export const AutocompleteListbox = forwardRef((props: Props, ref: Ref<HTMLDivElement>) => {
  const { children, internalListRef, onItemsBuilt, ...other } = props;
  const items: VirtualAutocompleteItem[] = [];
  const optionIndexMap = useMemo(() => new Map<string, number>(), []);

  // Flatten groups: push group header, then its children
  (children as VirtualAutocompleteItem[]).forEach(item => {
    items.push(item);
    if ("children" in item && Array.isArray(item.children)) {
      items.push(...(item.children as VirtualAutocompleteOptionParams[]));
    }
  });

  // Map labels to indices for keyboard navigation
  items.forEach((item, index) => {
    if (Array.isArray(item) && item[1]) {
      optionIndexMap.set(item[1], index);
    }
  });

  useEffect(() => {
    if (onItemsBuilt) {
      onItemsBuilt(optionIndexMap);
    }
  }, [onItemsBuilt, optionIndexMap]);

  const itemCount = items.length;

  const getChildSize = (child: VirtualAutocompleteItem) => {
    return "group" in child ? GROUP_HEADER_HEIGHT : OPTION_HEIGHT;
  };

  const getHeight = () => {
    return itemCount > 8 ? 8 * OPTION_HEIGHT : items.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  // Separate className for List, other props for wrapper div (ARIA, handlers)
  const { className, ...otherProps } = other;

  return (
    <div ref={ref} {...otherProps}>
      <List
        className={className}
        listRef={internalListRef}
        key={itemCount}
        rowCount={itemCount}
        rowHeight={index => getChildSize(items[index])}
        rowComponent={AutocompleteRow}
        rowProps={{ itemData: items }}
        style={{
          height: getHeight() + 2 * LISTBOX_PADDING,
          width: "100%",
        }}
        overscanCount={OVERSCAN_COUNT}
        tagName="ul"
      />
    </div>
  );
});
